import { create } from 'zustand';
import { Ambassador, FilterState } from '../types';
import { useMemo } from 'react';
import { shallow } from 'zustand/shallow';

interface FilterStore extends FilterState {
  // Actions
  setRoles: (roles: string[]) => void;
  setBusinessUnits: (businessUnits: string[]) => void;
  setCountries: (countries: string[]) => void;
  setActivities: (activities: string[]) => void;
  setSearchQuery: (query: string) => void;
  setLocationFilter: (ambassadorIds: number[]) => void;
  clearLocationFilter: () => void;
  clearFilters: () => void;
  
  // Individual filter setters (aliases for component compatibility)
  setRoleFilter: (roles: string[]) => void;
  setBusinessUnitFilter: (businessUnits: string[]) => void;
  setCountryFilter: (countries: string[]) => void;
  setActivityFilter: (activities: string[]) => void;
  clearAllFilters: () => void;
  
  // Computed state
  getFilteredAmbassadors: (ambassadors: Ambassador[]) => Ambassador[];
  getActiveFilterCount: () => number;
  filters: FilterState;
}

const initialFilterState: FilterState = {
  roles: [],
  businessUnits: [],
  countries: [],
  activities: [],
  searchQuery: '',
  locationFilter: undefined,
};

const useFiltersStore = create<FilterStore>((set, get) => ({
  ...initialFilterState,

  setRoles: (roles) => set({ roles }),
  setBusinessUnits: (businessUnits) => set({ businessUnits }),
  setCountries: (countries) => set({ countries }),
  setActivities: (activities) => set({ activities }),
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setLocationFilter: (ambassadorIds) => set({ locationFilter: ambassadorIds }),
  clearLocationFilter: () => set({ locationFilter: undefined }),
  
  clearFilters: () => set(initialFilterState),
  
  // Aliases for component compatibility
  setRoleFilter: (roles) => set({ roles }),
  setBusinessUnitFilter: (businessUnits) => set({ businessUnits }),
  setCountryFilter: (countries) => set({ countries }),
  setActivityFilter: (activities) => set({ activities }),
  clearAllFilters: () => set(initialFilterState),
  
  // Expose current filters state
  filters: initialFilterState,

  getFilteredAmbassadors: (ambassadors: Ambassador[]) => {
    const state = get();
    
    return ambassadors.filter((ambassador) => {
      // Location filter - if active, only show selected ambassadors
      if (state.locationFilter && state.locationFilter.length > 0) {
        return state.locationFilter.includes(ambassador.id);
      }

      // Business unit filter
      if (state.businessUnits.length > 0 && !state.businessUnits.includes(ambassador.businessUnit)) {
        return false;
      }

      // Country filter
      if (state.countries.length > 0 && !state.countries.includes(ambassador.country)) {
        return false;
      }

      // Activity filter
      if (state.activities.length > 0 && !state.activities.includes(ambassador.activity)) {
        return false;
      }

      // Search query filter
      if (state.searchQuery.trim()) {
        const query = state.searchQuery.toLowerCase();
        const searchableText = [
          ambassador.fullName,
          ambassador.city,
          ambassador.country,
          ambassador.businessUnit,
          ambassador.activity,
          ambassador.role,
          ambassador.region,
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(query)) {
          return false;
        }
      }

      return true;
    });
  },

  getActiveFilterCount: () => {
    const state = get();
    let count = 0;
    
    if (state.businessUnits.length > 0) count++;
    if (state.countries.length > 0) count++;
    if (state.activities.length > 0) count++;
    if (state.searchQuery.trim()) count++;
    if (state.locationFilter && state.locationFilter.length > 0) count++;
    
    return count;
  },
}));

// Optimized selectors for performance - Now with proper memoization
export const useFilteredAmbassadors = (ambassadors: Ambassador[]) => {
  // Split selectors to prevent unnecessary re-renders
  const businessUnits = useFiltersStore((state) => state.businessUnits, shallow);
  const countries = useFiltersStore((state) => state.countries, shallow);
  const activities = useFiltersStore((state) => state.activities, shallow);
  const searchQuery = useFiltersStore((state) => state.searchQuery);
  const locationFilter = useFiltersStore((state) => state.locationFilter, shallow);

  return useMemo(() => {
    return ambassadors.filter((ambassador) => {
      // Location filter - if active, only show selected ambassadors
      if (locationFilter && locationFilter.length > 0) {
        return locationFilter.includes(ambassador.id);
      }

      // Business unit filter - early exit for performance
      if (businessUnits.length > 0 && !businessUnits.includes(ambassador.businessUnit)) {
        return false;
      }

      // Country filter - early exit for performance
      if (countries.length > 0 && !countries.includes(ambassador.country)) {
        return false;
      }

      // Activity filter - early exit for performance
      if (activities.length > 0 && !activities.includes(ambassador.activity)) {
        return false;
      }

      // Search query filter - now optimized with pre-computed search blob
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        // Use pre-computed search blob if available, fallback to runtime computation
        const searchableText = ambassador.searchBlob || [
          ambassador.fullName,
          ambassador.city,
          ambassador.country,
          ambassador.businessUnit,
          ambassador.activity,
          ambassador.role,
          ambassador.region,
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [ambassadors, businessUnits, countries, activities, searchQuery, locationFilter]);
};

export const useFilterActions = () => 
  useFiltersStore((state) => ({
    setRoles: state.setRoles,
    setBusinessUnits: state.setBusinessUnits,
    setCountries: state.setCountries,
    setActivities: state.setActivities,
    setSearchQuery: state.setSearchQuery,
    setLocationFilter: state.setLocationFilter,
    clearLocationFilter: state.clearLocationFilter,
    clearFilters: state.clearFilters,
  }));

export const useFilterState = () => 
  useFiltersStore((state) => ({
    roles: state.roles,
    businessUnits: state.businessUnits,
    countries: state.countries,
    activities: state.activities,
    searchQuery: state.searchQuery,
    locationFilter: state.locationFilter,
  }));

// Export hook that includes current filter state
export const useFilterStore = () => {
  const store = useFiltersStore();
  return {
    ...store,
    filters: {
      roles: store.roles,
      businessUnits: store.businessUnits,
      countries: store.countries,
      activities: store.activities,
      searchQuery: store.searchQuery,
      locationFilter: store.locationFilter,
    },
  };
};

export { useFiltersStore as useFilters };